import React, { useState } from "react";
import { DatePickerWithRange } from "../../Components/ui/dateRange";
import DataTable from "../../Components/ui/dataTable";
import { Button } from "../../Components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../Components/ui/select";
import { parseMoney } from "../../Lib/utils";
import { Modal, ModalContent, ModalTrigger } from "../../Components/ui/modal";
import { useGetAllOrdersQuery } from "./OrderApi";
import { SkeletonTable } from "../../Components/ui/skeleton";
import Pagination from "../../Components/ui/pagination";
import OrderDetails from "./orderDetails";
import { format } from "date-fns";
import { useDebouncedValue } from "../../Lib/hooks";
import { ProtectedRoutes } from "../../config/protectedRoutes";

const STATUS_VARIANT_MAP = {
  pending: "pending",
  accepted: "accepted",
  picked_up: "picked_up",
  in_progress: "in_progress",
  delivered: "delivered",
  cancelled: "cancelled",
  declined: "declined",
  expired: "expired",
};

const PAYMENT_VARIANT_MAP = {
  paid: "successful",
  unpaid: "pending",
};

const Orders = () => {
  const today = new Date();
  const [date, setDate] = useState({
    from: today,
    to: today,
  });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");

  const debouncedSearch = useDebouncedValue(search, 500);

  const todayStr = format(today, "yyyy-MM-dd");
  const hasActiveFilters =
    search !== "" ||
    status !== "" ||
    paymentStatus !== "" ||
    (date?.from ? format(date.from, "yyyy-MM-dd") : "") !== todayStr ||
    (date?.to ? format(date.to, "yyyy-MM-dd") : "") !== todayStr;

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setPaymentStatus("");
    setDate({ from: today, to: today });
    setPage(1);
  };

  const { data, isLoading: loading } = useGetAllOrdersQuery(
    {
      page,
      customerName: debouncedSearch,
      status,
      paymentStatus,
      startDate: format(date?.from || new Date(), "yyyy-MM-dd"),
      endDate: format(date?.to || new Date(), "yyyy-MM-dd"),
    },
    {
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    },
  );
  const columns = [
    {
      accessorKey: "no",
      header: "No",
      cell: ({ row }) => (
        <div className='capitalize w-[60px]'>{row.getValue("no")}</div>
      ),
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ row }) => (
        <div className='font-semibold'>
          {parseMoney(row.getValue("amount"))}
        </div>
      ),
    },
    {
      accessorKey: "customerName",
      header: "Customer Name",
      cell: ({ row }) => (
        <div className='capitalize'>{row.getValue("customerName")}</div>
      ),
    },
    {
      accessorKey: "customerPhone",
      header: "Customer Phone",
      cell: ({ row }) => (
        <div className='capitalize'>{row.getValue("customerPhone")}</div>
      ),
    },
    {
      accessorKey: "item",
      header: "Item",
      cell: ({ row }) => (
        <div className='capitalize w-[100px]'>{row.getValue("item")}</div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const s = row.getValue("status");
        const variant = STATUS_VARIANT_MAP[s] || "pending";
        return (
          <Button asChild variant={variant} size='sm'>
            <p className='capitalize'>{s ? s.replace(/_/g, " ") : "N/A"}</p>
          </Button>
        );
      },
    },
    {
      accessorKey: "paymentStatus",
      header: "Payment Status",
      cell: ({ row }) => {
        const s = row.getValue("paymentStatus");
        const variant = PAYMENT_VARIANT_MAP[s] || "pending";
        return (
          <Button asChild variant={variant} size='sm'>
            <p className='capitalize'>{s || "N/A"}</p>
          </Button>
        );
      },
    },
    {
      accessorKey: "channel",
      header: "Channel",
      cell: ({ row }) => {
        const c = row.getValue("channel");
        return <div className='capitalize w-[120px]'>{c ?? "N/A"}</div>;
      },
    },

    {
      id: "action",
      cell: ({ row }) => {
        const detailsId = row.original.id;
        return (
          <Modal>
            <ModalContent
              name='Order Details'
              className='w-[92vw] max-w-[860px]'
            >
              <OrderDetails id={detailsId} />
            </ModalContent>
            <ModalTrigger>
              <Button
                variant='outline'
                className='outline-none focus:outline-none focus-visible:outline-none px-4 whitespace-nowrap'
              >
                View Details
              </Button>
            </ModalTrigger>
          </Modal>
        );
      },
    },
  ];
  return (
    <div className='bg-white p-4'>
      <div className='mb-4'>
        <h2 className='text-2xl font-bold text-primaryBlack'>Orders</h2>
        <p className='text-sm text-[#667499]'>
          Track and manage customer orders
        </p>
      </div>

      <div className='flex flex-wrap items-center gap-3 mb-4'>
        <div className='flex items-center bg-[#F5FEF1] w-full sm:w-[200px] p-1 rounded-sm border border-gray-100'>
          <input
            className='bg-transparent w-full px-1 outline-none placeholder:text-sm text-sm'
            value={search}
            placeholder='Search by customer name'
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Select
          value={status === "" ? "all" : status}
          onValueChange={(val) => {
            setStatus(val === "all" ? "" : val);
            setPage(1);
          }}
        >
          <SelectTrigger className='w-[150px] text-sm'>
            <SelectValue placeholder='All Status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Status</SelectItem>
            <SelectItem value='pending'>Pending</SelectItem>
            <SelectItem value='picked_up'>Picked Up</SelectItem>
            <SelectItem value='in_progress'>In Progress</SelectItem>
            <SelectItem value='delivered'>Delivered</SelectItem>
            <SelectItem value='cancelled'>Cancelled</SelectItem>
            <SelectItem value='failed'>Failed</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={paymentStatus === "" ? "all" : paymentStatus}
          onValueChange={(val) => {
            setPaymentStatus(val === "all" ? "" : val);
            setPage(1);
          }}
        >
          <SelectTrigger className='w-[170px] text-sm'>
            <SelectValue placeholder='All Payment Status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Payment Status</SelectItem>
            <SelectItem value='paid'>Paid</SelectItem>
            <SelectItem value='unpaid'>Unpaid</SelectItem>
          </SelectContent>
        </Select>

        <DatePickerWithRange date={date} setDate={setDate} />

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className='text-sm text-red-500 hover:text-red-700 underline whitespace-nowrap'
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className='table-scroll max-h-[70vh] overflow-auto max-w-full rounded-md border'>
        <div className='w-full'>
          {loading ? (
            <SkeletonTable />
          ) : (
            <DataTable columns={columns} data={data?.data} />
          )}
        </div>
      </div>
      {!loading && (
        <Pagination
          page={page}
          limit={data?.limit ?? 0}
          total={data?.total ?? 0}
          setPage={setPage}
        />
      )}
    </div>
  );
};

export default ProtectedRoutes(Orders, [3, 5, 2, 1]);
